import { UserName } from "../components/UserName.tsx";
import { useCallback, useEffect, useState } from "react";
import { MAX_ATTEMPTS, type DuelHistoryEntry, type RaceOver, type TeamHistoryEntry, type TimedHistoryEntry } from "@mk/shared";
import { ResultBoard } from "../components/ResultBoard.tsx";
import { ScreenHeader } from "../components/ScreenHeader.tsx";
import { Sheet } from "../components/Sheet.tsx";
import { TimedRunReport } from "../components/TimedRunReport.tsx";
import { duelResultText } from "../features/duel/resultText.ts";
import { duelRulesSummary } from "../features/duel/rules.ts";
import { loadPracticeHistory, type PracticeRecord } from "../features/solo/practiceHistory.ts";
import { messages } from "../i18n/index.ts";
import { api } from "../lib/api.ts";
import { formatDate } from "../lib/edition.ts";
import type { RegisterBackHandler } from "../lib/navigation.ts";
import { useOnline } from "../lib/pwa.ts";
import { useSession } from "../lib/session.ts";

export type HistoryTab = "competitive" | "solo" | "timed" | "friendly" | "race" | "team";
type RemoteHistoryTab = "competitive" | "friendly";
type Outcome = "win" | "loss" | "draw";

interface HistoryScreenProps {
  onBack: () => void;
  onSignIn: (reason: string) => void;
  registerBackHandler: RegisterBackHandler;
  /** Eski `practice-history` gezinme durumunu Tek Başına sekmesine taşır. */
  initialTab?: HistoryTab;
}

interface RemoteHistoryState {
  entries: DuelHistoryEntry[] | null;
  loading: boolean;
  error: boolean;
  retry: () => void;
}

interface TeamHistoryState {
  entries: TeamHistoryEntry[] | null;
  loading: boolean;
  error: boolean;
  retry: () => void;
}

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
};

const OUTCOME_LABEL: Record<Outcome, string> = messages.friendHistory.badge;
const OUTCOME_TITLE: Record<Outcome, string> = {
  win: messages.friendHistory.win,
  loss: messages.friendHistory.loss,
  draw: messages.friendHistory.draw,
};
const OUTCOME_ACCENT: Record<Outcome, "mint" | "flame" | "amber"> = {
  win: "mint",
  loss: "flame",
  draw: "amber",
};

function outcomeOf(entry: DuelHistoryEntry, meId: string | undefined): Outcome {
  if (entry.winnerId === null) return "draw";
  return entry.winnerId === meId ? "win" : "loss";
}

function useRemoteHistory(category: RemoteHistoryTab, enabled: boolean): RemoteHistoryState {
  const [entries, setEntries] = useState<DuelHistoryEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retryVersion, setRetryVersion] = useState(0);

  useEffect(() => {
    if (!enabled || entries !== null) return;
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError(false);
    api<{ entries: DuelHistoryEntry[] }>(
      `/api/duels/history?category=${category}&limit=10`,
      { signal: controller.signal },
    )
      .then((data) => {
        if (!cancelled) setEntries(data.entries);
      })
      .catch((reason: unknown) => {
        if (!cancelled && !(reason instanceof DOMException && reason.name === "AbortError")) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
    // `retryVersion` bilinçli olarak yeni bir istek başlatır.
  }, [category, enabled, entries, retryVersion]);

  return {
    entries,
    loading,
    error,
    retry: () => {
      setError(false);
      setEntries(null);
      setRetryVersion((value) => value + 1);
    },
  };
}

function useTeamHistory(enabled: boolean): TeamHistoryState {
  const [entries, setEntries] = useState<TeamHistoryEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retryVersion, setRetryVersion] = useState(0);
  useEffect(() => {
    if (!enabled || entries !== null) return;
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    api<{ entries: TeamHistoryEntry[] }>("/api/team-matches/history?limit=10", { signal: controller.signal })
      .then((data) => setEntries(data.entries))
      .catch((reason: unknown) => {
        if (!(reason instanceof DOMException && reason.name === "AbortError")) setError(true);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [enabled, entries, retryVersion]);
  return { entries, loading, error, retry: () => { setEntries(null); setError(false); setRetryVersion((value) => value + 1); } };
}

export function HistoryScreen({
  onBack,
  onSignIn,
  registerBackHandler,
  initialTab,
}: HistoryScreenProps) {
  const status = useSession((state) => state.status);
  const meId = useSession((state) => state.user?.id);
  const online = useOnline();
  const [activeTab, setActiveTab] = useState<HistoryTab>(() =>
    initialTab ?? (status === "authed" && online ? "competitive" : "solo"),
  );
  const [soloEntries] = useState<PracticeRecord[]>(() => loadPracticeHistory());
  const [selectedDuel, setSelectedDuel] = useState<DuelHistoryEntry | null>(null);
  const [selectedSolo, setSelectedSolo] = useState<PracticeRecord | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<TeamHistoryEntry | null>(null);
  const [selectedRace, setSelectedRace] = useState<RaceOver | null>(null);
  const [raceEntries, setRaceEntries] = useState<RaceOver[] | null>(null);
  const [raceLoading, setRaceLoading] = useState(false);
  const [raceError, setRaceError] = useState(false);
  const [raceRetry, setRaceRetry] = useState(0);
  const [selectedTimed, setSelectedTimed] = useState<TimedHistoryEntry | null>(null);
  const [timedEntries, setTimedEntries] = useState<TimedHistoryEntry[] | null>(null);
  const [timedLoading, setTimedLoading] = useState(false);
  const [timedError, setTimedError] = useState(false);
  const [timedRetry, setTimedRetry] = useState(0);

  const canLoadRemote = status === "authed" && online;
  const competitive = useRemoteHistory(
    "competitive",
    canLoadRemote && activeTab === "competitive",
  );
  const friendly = useRemoteHistory(
    "friendly",
    canLoadRemote && activeTab === "friendly",
  );
  const team = useTeamHistory(canLoadRemote && activeTab === "team");

  useEffect(() => {
    if (!canLoadRemote || activeTab !== "race" || raceEntries !== null) return;
    const controller = new AbortController();
    setRaceLoading(true);
    setRaceError(false);
    api<{ entries: RaceOver[] }>("/api/races/history?limit=10", { signal: controller.signal })
      .then((data) => setRaceEntries(data.entries))
      .catch((reason: unknown) => {
        if (!(reason instanceof DOMException && reason.name === "AbortError")) setRaceError(true);
      })
      .finally(() => setRaceLoading(false));
    return () => controller.abort();
  }, [activeTab, canLoadRemote, raceEntries, raceRetry]);

  useEffect(() => {
    if (!canLoadRemote || activeTab !== "timed" || timedEntries !== null) return;
    const controller = new AbortController();
    setTimedLoading(true);
    setTimedError(false);
    api<{ entries: TimedHistoryEntry[] }>("/api/solo/timed/history?limit=10", { signal: controller.signal })
      .then((data) => setTimedEntries(data.entries))
      .catch((reason: unknown) => {
        if (!(reason instanceof DOMException && reason.name === "AbortError")) setTimedError(true);
      })
      .finally(() => setTimedLoading(false));
    return () => controller.abort();
  }, [activeTab, canLoadRemote, timedEntries, timedRetry]);

  const closeDetail = useCallback(() => {
    setSelectedDuel(null);
    setSelectedTimed(null);
    setSelectedSolo(null);
    setSelectedTeam(null);
    setSelectedRace(null);
  }, []);

  useEffect(() => {
    if (!selectedDuel && !selectedSolo && !selectedTeam && !selectedRace && !selectedTimed) return;
    return registerBackHandler(() => {
      closeDetail();
      return "block";
    });
  }, [closeDetail, registerBackHandler, selectedDuel, selectedSolo, selectedTeam, selectedRace, selectedTimed]);

  function selectTab(tab: HistoryTab) {
    closeDetail();
    setActiveTab(tab);
  }

  function remotePanel(state: RemoteHistoryState, empty: string) {
    if (status !== "authed") {
      return (
        <div className="history-gate">
          <p className="font-display text-lg">{messages.duelHistory.accountRequired}</p>
          <p className="text-sm leading-relaxed text-muted">
            {messages.duelHistory.accountRequiredHint}
          </p>
          <button
            type="button"
            className="btn-primary"
            onClick={() => onSignIn(messages.home.historyReason)}
          >
            {messages.duelHistory.signIn}
          </button>
        </div>
      );
    }

    if (!online) {
      return (
        <div className="history-gate">
          <p className="font-display text-lg">{messages.duelHistory.connectionRequired}</p>
          <p className="text-sm leading-relaxed text-muted">
            {messages.duelHistory.connectionRequiredHint}
          </p>
        </div>
      );
    }

    if (state.loading && state.entries === null) {
      return <p className="text-sm text-muted">{messages.common.loading}</p>;
    }

    if (state.error) {
      return (
        <div className="history-gate">
          <p className="text-sm text-muted">{messages.duelHistory.loadFailed}</p>
          <button type="button" className="btn-secondary" onClick={state.retry}>
            {messages.common.tryAgain}
          </button>
        </div>
      );
    }

    if (state.entries?.length === 0) {
      return <p className="text-sm leading-relaxed text-muted">{empty}</p>;
    }

    return state.entries?.map((entry) => {
      const outcome = outcomeOf(entry, meId);
      return (
        <button
          key={entry.matchId}
          type="button"
          className="history-row"
          data-outcome={outcome}
          onClick={() => setSelectedDuel(entry)}
        >
          <span className="history-row__badge">{OUTCOME_LABEL[outcome]}</span>
          <span className="history-row__main">
            <span className="history-row__name">{<UserName user={entry.opponent.user} />}</span>
            <span className="history-row__date">
              {formatDate(entry.finishedAt, DATE_OPTIONS)}
            </span>
          </span>
          <span className="history-row__score">
            {entry.you.solved ? entry.you.attempts : "—"}
            <span aria-hidden="true"> : </span>
            {entry.opponent.solved ? entry.opponent.attempts : "—"}
          </span>
        </button>
      );
    });
  }

  const selectedOutcome = selectedDuel ? outcomeOf(selectedDuel, meId) : null;

  return (
    <div className="app">
      <ScreenHeader
        title={messages.duelHistory.title}
        subtitle={messages.duelHistory.lastTen}
        onBack={onBack}
      />

      <main className="overflow-y-auto px-4 pb-5">
        <div className="mx-auto flex w-full max-w-md flex-col gap-4">
          <div className="history-tabs" role="tablist" aria-label={messages.duelHistory.tabsLabel}>
            {(
              [
                ["competitive", messages.duelHistory.tabs.competitive],
                ["solo", messages.duelHistory.tabs.solo],
                ["timed", messages.duelHistory.tabs.timed],
                ["friendly", messages.duelHistory.tabs.friendly],
                ["race", messages.duelHistory.tabs.race],
                ["team", messages.duelHistory.tabs.team],
              ] as const
            ).map(([tab, label]) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={activeTab === tab}
                className="history-tab"
                onClick={() => selectTab(tab)}
              >
                {label}
              </button>
            ))}
          </div>

          <section
            className="flex flex-col gap-2.5"
            role="tabpanel"
            aria-label={messages.duelHistory.tabs[activeTab]}
          >
            {activeTab === "competitive" &&
              remotePanel(competitive, messages.duelHistory.competitiveEmpty)}

            {activeTab === "friendly" &&
              remotePanel(friendly, messages.duelHistory.friendlyEmpty)}

            {activeTab === "race" && status !== "authed" && <div className="history-gate"><p>{messages.duelHistory.accountRequired}</p><button type="button" className="btn-primary" onClick={() => onSignIn(messages.home.historyReason)}>{messages.duelHistory.signIn}</button></div>}
            {activeTab === "race" && status === "authed" && !online && <div className="history-gate"><p>{messages.duelHistory.connectionRequired}</p></div>}
            {activeTab === "race" && canLoadRemote && raceLoading && raceEntries === null && <p className="text-sm text-muted">{messages.common.loading}</p>}
            {activeTab === "race" && canLoadRemote && raceError && <div className="history-gate"><p>{messages.duelHistory.loadFailed}</p><button type="button" className="btn-secondary" onClick={() => { setRaceEntries(null); setRaceRetry((value) => value + 1); }}>{messages.common.tryAgain}</button></div>}
            {activeTab === "race" && canLoadRemote && raceEntries?.length === 0 && <p className="text-sm text-muted">{messages.duelHistory.raceEmpty}</p>}
            {activeTab === "race" && canLoadRemote && raceEntries?.map((entry) => {
              const outcome = entry.winnerId === null ? "draw" : entry.winnerId === meId ? "win" : "loss";
              return <button key={entry.matchId} type="button" className="history-row" data-outcome={outcome} onClick={() => setSelectedRace(entry)}>
                <span className="history-row__badge">{OUTCOME_LABEL[outcome]}</span>
                <span className="history-row__main"><span className="history-row__name"><UserName user={entry.opponent} /></span><span className="history-row__date">{formatDate(entry.finishedAt, DATE_OPTIONS)}</span></span>
                <span className="history-row__score">{entry.you.wordsSolved} : {entry.rival.wordsSolved}</span>
              </button>;
            })}

            {activeTab === "team" && status !== "authed" && (
              <div className="history-gate"><p className="font-display text-lg">{messages.duelHistory.accountRequired}</p><button type="button" className="btn-primary" onClick={() => onSignIn(messages.home.historyReason)}>{messages.duelHistory.signIn}</button></div>
            )}
            {activeTab === "team" && status === "authed" && !online && (
              <div className="history-gate"><p className="font-display text-lg">{messages.duelHistory.connectionRequired}</p><p className="text-sm text-muted">{messages.duelHistory.connectionRequiredHint}</p></div>
            )}
            {activeTab === "team" && status === "authed" && online && team.loading && team.entries === null && <p className="text-sm text-muted">{messages.common.loading}</p>}
            {activeTab === "team" && status === "authed" && online && team.error && <div className="history-gate"><p>{messages.duelHistory.loadFailed}</p><button type="button" className="btn-secondary" onClick={team.retry}>{messages.common.tryAgain}</button></div>}
            {activeTab === "team" && team.entries?.length === 0 && <p className="text-sm text-muted">{messages.duelHistory.teamEmpty}</p>}
            {activeTab === "team" && team.entries?.map((entry) => {
              const mine = entry.players.find((player) => player.user.id === meId);
              const outcome = entry.winnerTeam === null ? "draw" : entry.winnerTeam === mine?.team ? "win" : "loss";
              return <button key={entry.matchId} type="button" className="history-row" data-outcome={outcome} onClick={() => setSelectedTeam(entry)}>
                <span className="history-row__badge">{OUTCOME_LABEL[outcome]}</span>
                <span className="history-row__main"><span className="history-row__name">{messages.team.title} · {entry.lobbyCode}</span><span className="history-row__date">{formatDate(entry.finishedAt, DATE_OPTIONS)}</span></span>
                <span className="history-row__score">{entry.score.A.words} : {entry.score.B.words}</span>
              </button>;
            })}

            {activeTab === "timed" && status !== "authed" && (
              <div className="history-gate"><p className="font-display text-lg">{messages.duelHistory.accountRequired}</p><button type="button" className="btn-primary" onClick={() => onSignIn(messages.home.historyReason)}>{messages.duelHistory.signIn}</button></div>
            )}
            {activeTab === "timed" && status === "authed" && !online && (
              <div className="history-gate"><p className="font-display text-lg">{messages.duelHistory.connectionRequired}</p><p className="text-sm text-muted">{messages.duelHistory.connectionRequiredHint}</p></div>
            )}
            {activeTab === "timed" && canLoadRemote && timedLoading && timedEntries === null && <p className="text-sm text-muted">{messages.common.loading}</p>}
            {activeTab === "timed" && canLoadRemote && timedError && <div className="history-gate"><p>{messages.duelHistory.loadFailed}</p><button type="button" className="btn-secondary" onClick={() => { setTimedEntries(null); setTimedRetry((value) => value + 1); }}>{messages.common.tryAgain}</button></div>}
            {activeTab === "timed" && canLoadRemote && timedEntries?.length === 0 && <p className="text-sm leading-relaxed text-muted">{messages.duelHistory.timedEmpty}</p>}
            {activeTab === "timed" && canLoadRemote && timedEntries?.map((entry) => (
              <button key={entry.runId} type="button" className="history-row" data-outcome={entry.wordsSolved > 0 ? "win" : "draw"} onClick={() => setSelectedTimed(entry)}>
                <span className="history-row__badge">{entry.wordsSolved}</span>
                <span className="history-row__main">
                  <span className="history-row__name">{messages.solo.rankingScore(entry.wordsSolved, entry.solvedAttempts)}</span>
                  <span className="history-row__date">{formatDate(entry.finishedAt, DATE_OPTIONS)}</span>
                </span>
              </button>
            ))}

            {activeTab === "solo" && soloEntries.length === 0 && (
              <p className="text-sm leading-relaxed text-muted">
                {messages.solo.historyEmpty}
              </p>
            )}

            {activeTab === "solo" &&
              soloEntries.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className="history-row"
                  data-outcome={entry.solved ? "win" : "loss"}
                  onClick={() => setSelectedSolo(entry)}
                >
                  <span className="history-row__badge">{entry.solved ? "✓" : "✕"}</span>
                  <span className="history-row__main">
                    <span className="history-row__name">{entry.answer}</span>
                    <span className="history-row__date">
                      {formatDate(entry.finishedAt, DATE_OPTIONS)}
                    </span>
                  </span>
                  <span className="history-row__score">
                    {entry.solved ? `${entry.guesses.length}/${MAX_ATTEMPTS}` : "—"}
                  </span>
                </button>
              ))}
          </section>
        </div>
      </main>

      <Sheet
        open={selectedDuel !== null}
        title={selectedOutcome ? OUTCOME_TITLE[selectedOutcome] : ""}
        accent={selectedOutcome ? OUTCOME_ACCENT[selectedOutcome] : "mint"}
        placement="center"
        onClose={closeDetail}
      >
        {selectedDuel && (
          <>
            <p className="text-sm text-muted">{duelResultText(selectedDuel, meId)}</p>
            <p className="mt-1 text-xs text-dim">
              {formatDate(selectedDuel.finishedAt, DATE_OPTIONS)}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-muted">
              {selectedDuel.category === "friendly"
                ? messages.common.friendlyMatch
                : messages.common.standardDuel}{" "}
              · {duelRulesSummary(selectedDuel.rules)}
            </p>

            <div className="answer-row mt-4">
              {[...selectedDuel.answer].map((letter, index) => (
                <span key={index} className="tile" data-filled="true" data-state="correct">
                  {letter}
                </span>
              ))}
            </div>

            <div className="duel-result-grid mt-6">
              <div className="duel-result-card plate-l">
                <span className="duel-compare__name">
                  {messages.common.you}{" "}
                  <span className="text-mint">· {selectedDuel.you.attempts}</span>
                </span>
                <ResultBoard rows={selectedDuel.you.guesses} />
              </div>
              <div className="duel-result-card plate-r">
                <span className="duel-compare__name">
                  {<UserName user={selectedDuel.opponent.user} />}{" "}
                  <span className="text-flame">· {selectedDuel.opponent.attempts}</span>
                </span>
                <ResultBoard rows={selectedDuel.opponent.guesses} />
              </div>
            </div>

            <button type="button" className="btn-secondary mt-6 w-full" onClick={closeDetail}>
              {messages.common.close}
            </button>
          </>
        )}
      </Sheet>

      <Sheet open={selectedTeam !== null} title={messages.team.matchOver} accent="amber" placement="center" onClose={closeDetail}>
        {selectedTeam && <>
          <div className="team-result-score"><b>A {selectedTeam.score.A.words}</b><span>:</span><b>{selectedTeam.score.B.words} B</b></div>
          <p className="mt-2 text-center text-xs text-muted">{messages.team.minutes(selectedTeam.settings.durationMinutes)} · {selectedTeam.lobbyCode}</p>
          <div className="team-history-detail mt-5">
            {selectedTeam.players.map((player) => <article key={player.user.id} data-team={player.team}>
              <h3>{<UserName user={player.user} />} · {player.team}{player.lane}</h3>
              <p>{player.wordsSolved} {messages.team.words} · {player.completedAttempts} {messages.team.attempts}</p>
              {player.rounds.map((round) => <div key={round.wordIndex} className="team-round-result"><b>{round.answer}</b><ResultBoard rows={round.guesses} /></div>)}
            </article>)}
          </div>
          <button type="button" className="btn-secondary mt-5 w-full" onClick={closeDetail}>{messages.common.close}</button>
        </>}
      </Sheet>

      <Sheet open={selectedRace !== null} title={messages.race.resultTitle} accent="amber" placement="center" onClose={closeDetail}>
        {selectedRace && <>
          <p className="text-sm text-muted"><UserName user={selectedRace.opponent} /> · {formatDate(selectedRace.finishedAt, DATE_OPTIONS)}</p>
          <div className="team-result-score"><b>{selectedRace.you.wordsSolved}</b><span>:</span><b>{selectedRace.rival.wordsSolved}</b></div>
          <p className="text-sm text-muted">{messages.race.attempts(selectedRace.you.solvedAttempts, selectedRace.rival.solvedAttempts)}</p>
          <div className="team-history-detail mt-5">{selectedRace.you.rounds.map((round) => <article key={round.wordIndex}><h3>{round.wordIndex + 1}. {round.answer}</h3><ResultBoard rows={round.guesses} /></article>)}</div>
          <button type="button" className="btn-secondary mt-5 w-full" onClick={closeDetail}>{messages.common.close}</button>
        </>}
      </Sheet>

      <TimedRunReport run={selectedTimed} onClose={closeDetail} />

      <Sheet
        open={selectedSolo !== null}
        title={selectedSolo?.solved ? messages.solo.found : messages.solo.missed}
        accent={selectedSolo?.solved ? "mint" : "flame"}
        placement="center"
        onClose={closeDetail}
      >
        {selectedSolo && (
          <>
            <p className="text-sm text-muted">
              {selectedSolo.solved
                ? messages.solo.foundIn(selectedSolo.guesses.length)
                : messages.solo.allAttemptsUsed(MAX_ATTEMPTS)}
            </p>
            <p className="mt-1 text-xs text-dim">
              {formatDate(selectedSolo.finishedAt, DATE_OPTIONS)}
            </p>

            <div className="result-stack">
              <div className="answer-row">
                {[...selectedSolo.answer].map((letter, index) => (
                  <span key={index} className="tile" data-filled="true" data-state="correct">
                    {letter}
                  </span>
                ))}
              </div>
              <ResultBoard rows={selectedSolo.guesses} />
            </div>

            <button type="button" className="btn-secondary mt-5 w-full" onClick={closeDetail}>
              {messages.common.close}
            </button>
          </>
        )}
      </Sheet>
    </div>
  );
}
